import type {
  CreateCallOffItemResponse,
  CreateCallOffItemVariables,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodCreateCallOffItemPayloadSchema,
  zodCreateCallOffItemResponseSchema,
} from './validations';

/**
 * Adds a new line item to an existing call-off.
 *
 * @param variables - The call-off ID and item data to create.
 * @returns The created call-off item with its generated ID and metadata.
 * @throws Error if the API request fails or validation fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/create_calloff_item_calloffs__calloff_id__items_post
 */
export const createCallOffItem = async (
  variables: CreateCallOffItemVariables,
) => {
  const { callOffId, payload } = variables;

  const response = await vestigasApi
    .post(ENDPOINT.CALL_OFF.CREATE_ITEM(callOffId), {
      json: payload,
      validate: {
        body: zodCreateCallOffItemPayloadSchema,
      },
    })
    .json<CreateCallOffItemResponse>();

  // Validate the response with Zod
  const validatedResponse =
    zodCreateCallOffItemResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Create CallOff Item API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
